import type {
  ResponseError,
  ListTicketsV2Request,
  TicketList,
} from '@rtkwlf/rendall';
import type {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useSelectedOrganization } from '@packages/legacy/organization-context';
import { ticketKeys } from '@packages/utils/queryKeyFactories/tickets';

import { useRendallApi } from '../use-rendall-api';

export type GetTicketListParams = Omit<
  ListTicketsV2Request,
  'organizationId' | 'page'
>;

export const getTicketListQueryKey = (
  organizationId: string,
  params: GetTicketListParams,
) =>
  // TODO: review keys -- current implementation sets the orgId lasts
  // Need to keep in sync for now because this is what is used inside the Tickets feature
  ticketKeys.list(organizationId, {
    after: params.after,
    before: params.before,
    includeInactive: params.includeInactive,
  });

export const useGetTicketList = (
  params: GetTicketListParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      TicketList,
      ResponseError,
      InfiniteData<TicketList, number>,
      TicketList,
      QueryKey,
      number
    >,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  const api = useRendallApi();
  const organization = useSelectedOrganization();

  return useInfiniteQuery({
    queryKey: getTicketListQueryKey(organization.id, params),
    queryFn: ({ pageParam }) =>
      api.listTicketsV2({
        ...params,
        organizationId: organization.id,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.next ? parseInt(lastPage.next, 10) : undefined;
    },
    ...options,
  });
};
